/**
 * secrets-plugin — the `/secrets` secrets-store admin surface as a studio-sibling
 * plugin (#159 S1).
 *
 * The visible secrets admin/browse surface: a `ComposableGenomePlugin` mirroring
 * `storePlugin` / `agentPlugin` — one `component` editor (`vault`) routed at
 * `/secrets`, composed into the ONE studio genome (gate 4 — `/secrets` resolves to
 * the registered `vault` editor). The editor is `React.lazy` so the whole secrets
 * surface stays in a split chunk OUT of the app entry (gate 8 — entry < 150 KB gz).
 *
 * Adds no new `EntityKind` (the surface lowers onto `component`/`route`), so gate 4
 * stays green. Unlike `storePlugin`, it does NOT register a dock panel: the vault
 * is a PER-USER admin surface with its own lock/RBAC state and no host
 * `connectionRef` dependency, so it has no place in the host-scoped IDE chrome
 * (design Q1).
 *
 * @module @ubiclouds/v5-studio/secrets/secrets-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The secrets-surface plugin id. */
export const SECRETS_PLUGIN_ID = 'v5-studio-secrets';

/** The split-chunk secrets surface (the `/secrets` route editor). */
const LazySecretsVault = lazy(() => import('./SecretsVault.js'));

/**
 * The secrets plugin — the `/secrets` route → the lazy `vault` editor. Composed as
 * a SIBLING in `studioPlugins`; adds no new `EntityKind` (gate 4 holds).
 */
export const secretsPlugin: ComposableGenomePlugin = definePlugin(SECRETS_PLUGIN_ID)
  .name('Secrets')
  .trustTier('core')
  .editor('vault', LazySecretsVault)
  .route('/secrets', { routesTo: 'component/vault', title: 'Secrets' })
  .build();
